// One field of a clinician's edit screen, drawn as an unsaved change while its
// value differs from the value last saved (TGCLOUD-1902). The ONE way an edit
// screen shows it (rule 9): wrap the field, whatever input it is.
//
//   <ChangedField name="lastName" changed={changes.changed.lastName}>
//     <TextInput label="Last Name" … />
//   </ChangedField>
//
// While `changed` (useChangedFields' entry for the field, or true with `was`):
//   - the field's inputs take the gold ground and outline (palette.changed);
//   - a "Changed" label sits at its top right: the cue that is not a colour
//     (rule 3). There is no bar beside the field (Tim, 2026-10-13);
//   - "Was: …" shows on hover and on focus, "Was: empty" for no value (rule 4);
//   - its inputs are described to a screen reader as "Changed, was …".
// An input in ERROR keeps its red outline and its helper text (rule 8). The
// label stays: with no bar, it is the one cue left that is not a colour. In
// create mode, or disabled, nothing is drawn (rules 6 and 8).
//
// The element tree is the same whether or not the field is changed, so the input
// is never remounted, and never loses focus, as the first keystroke turns it gold.

import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import useDateFormatter from 'src/hooks/useDateFormatter';
import { formatFieldValue } from './formatFieldValue';
import { VISUALLY_HIDDEN, changedSx } from './changedFieldStyles';
import ChangedFieldLabel from './ChangedFieldLabel';

// The elements a clinician types into or chooses with: each is described as
// changed. MUI's hidden inputs (a select's value, a multiline's shadow) are not.
const DESCRIBED =
  'input:not([type="hidden"]):not([aria-hidden="true"]), ' +
  'textarea:not([aria-hidden="true"]), select, ' +
  '[role="combobox"]:not(input), [contenteditable="true"]';

const tokens = (el, attr) =>
  (el.getAttribute(attr) || '').split(/\s+/).filter(Boolean);

const addToken = (el, attr, token) => {
  const list = tokens(el, attr);
  if (!list.includes(token)) el.setAttribute(attr, [...list, token].join(' '));
};

const removeToken = (el, attr, token) => {
  const list = tokens(el, attr).filter((t) => t !== token);
  if (list.length) el.setAttribute(attr, list.join(' '));
  else el.removeAttribute(attr);
};

/**
 * @param {object} props
 * @param {string} props.name  the field's name: what "jump to the next change" finds it by
 * @param {boolean|{was: *, now: *}} props.changed  useChangedFields' entry, or a flag
 * @param {*} [props.was]  the value as last saved, when `changed` is a flag
 * @param {'edit'|'create'} [props.mode]  nothing is drawn in create mode
 * @param {boolean} [props.disabled]  recalculated or read-only: never drawn
 * @param {'field'|'control'|'row'|'tile'|'cell'} [props.variant]  'cell' is a
 *   table row's first cell: the row draws the gold (changedSx 'row'), the
 *   cell says it, since a <tr> may hold nothing but cells
 * @param {Function} [props.formatWas]  (was) => string, for a value the default cannot read
 * @param {Array} [props.options]  the field's options: "Was:" shows a value's label
 * @param {boolean} [props.translateOptions]  false when the options are data
 * @param {string} [props.kind]  'flag', 'date' or 'datetime', as the comparator takes it
 * @param {boolean} [props.showLabel]  the visible "Changed" label (rows: none)
 * @param {'top-end'|'bottom-end'} [props.labelPlacement]
 * @param {React.ElementType} [props.component]  the element it renders as ('div')
 * @param {string} [props.scope]  a part of the form with a Save of its own: its
 *   changes are not the form's, so the form's count and "next change" skip them
 */
export default function ChangedField({
  name,
  changed,
  was,
  mode = 'edit',
  disabled = false,
  variant = 'field',
  formatWas,
  options,
  translateOptions = true,
  kind,
  showLabel,
  labelPlacement,
  component = 'div',
  scope,
  sx,
  children,
  ...rest
}) {
  const t = useAutoTranslation();
  const fmtDate = useDateFormatter();
  const theme = useTheme();
  const descriptionId = `${React.useId()}changed`;
  const rootRef = React.useRef(null);

  const active = mode !== 'create' && !disabled && Boolean(changed);
  const previous =
    changed && typeof changed === 'object' && 'was' in changed
      ? changed.was
      : was;

  let wasText = '';
  let description = '';
  if (active) {
    const shown = formatWas
      ? formatWas(previous)
      : formatFieldValue(previous, {
          t,
          fmtDate,
          options,
          translateOptions,
          kind
        });
    if (shown === null || shown === '') {
      wasText = t('Was: empty');
      description = t('Changed, was empty');
    } else if (shown === undefined) {
      wasText = t('Changed from the saved value');
      description = t('Changed from the saved value');
    } else {
      wasText = t('Was: {{value}}', { value: shown });
      description = t('Changed, was {{value}}', { value: shown });
    }
  }

  // Describe every input the field holds as changed, while it is: they belong to
  // whichever wrapper (TextInput, a raw MUI control) drew them, so the
  // description is added to them here rather than passed down. After every
  // render, because an input can be replaced by the one that drew it.
  React.useLayoutEffect(() => {
    const root = rootRef.current;
    if (!active || !root) return undefined;
    const described = Array.from(root.querySelectorAll(DESCRIBED));
    described.forEach((el) => addToken(el, 'aria-describedby', descriptionId));
    return () =>
      described.forEach((el) =>
        removeToken(el, 'aria-describedby', descriptionId)
      );
  });

  const labelled = showLabel ?? (variant !== 'row' && variant !== 'cell');
  const placement =
    labelPlacement ?? (variant === 'tile' ? 'bottom-end' : 'top-end');

  return (
    <Tooltip
      title={wasText}
      describeChild
      placement="top-start"
      enterDelay={500}
      enterNextDelay={200}
      disableInteractive
    >
      <Box
        ref={rootRef}
        component={component}
        data-changed-field={name}
        data-changed={active ? 'true' : undefined}
        data-changed-scope={scope}
        sx={[
          changedSx(theme, { active, variant }),
          ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
        ]}
        {...rest}
      >
        {children}
        <ChangedFieldLabel show={active && labelled} placement={placement} />
        {active && (
          <Box component="span" id={descriptionId} sx={VISUALLY_HIDDEN}>
            {description}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}
